import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

import type { ConnectionStatusType } from "@/models/ConnectionStatusModel";

const statusConfig: Record<
  ConnectionStatusType,
  {
    color?: string;
    icon?: React.ReactNode;
    messageKey:
      | "connection.noConnection"
      | "connection.offline"
      | "connection.offlineUnsynced"
      | "connection.online"
      | "connection.onlineUnsynced"
      | "connection.reconnecting";
  }
> = {
  "no-connection": {
    messageKey: "connection.noConnection",
  },
  offline: {
    color: "text-gray-600",
    icon: <Icon name="Offline" />,
    messageKey: "connection.offline",
  },
  "offline-unsynced": {
    color: "text-orange-600",
    icon: <Icon name="Offline" />,
    messageKey: "connection.offlineUnsynced",
  },
  "online-unsynced": {
    color: "text-green-600",
    icon: <Icon name="Reconnect" />,
    messageKey: "connection.onlineUnsynced",
  },
  online: {
    color: "text-green-600",
    icon: <Icon name="Online" />,
    messageKey: "connection.online",
  },
  reconnecting: {
    color: "text-yellow-600",
    icon: <Icon name="Reconnect" />,
    messageKey: "connection.reconnecting",
  },
};

/**
 * The document's connection state, shown while it is not plainly online. The status region stays
 * mounted and only its text changes, since screen readers often skip a live region that appears
 * already filled; a collapsed sidebar keeps it for them with `visuallyHidden`.
 */
export const ConnectionStatus = observer(function ConnectionStatus({
  withBottomBorder,
  visuallyHidden,
}: {
  withBottomBorder?: boolean;
  visuallyHidden?: boolean;
}) {
  const t = useTranslations();
  const { doc } = useStore();

  const connectionStatus = doc?.connectionStatus;
  const config = connectionStatus?.showCurrentStatus
    ? statusConfig[connectionStatus.status]
    : undefined;
  const isVisible = !!config && !visuallyHidden;

  return (
    <>
      <div
        role="status"
        className={twMerge(
          visuallyHidden ? "sr-only" : "flex flex-1 flex-row items-center",
          isVisible && "min-h-[38px]",
        )}
      >
        {config && (
          <>
            <span aria-hidden className="mr-1 ml-1.25">
              {config.icon}
            </span>
            <span className="text-size-15 tracking-tight">
              {t(config.messageKey)}
              {config.messageKey === "connection.offlineUnsynced" && (
                <span className="text-gray-500">
                  &nbsp;·&nbsp;{t("connection.offlineUnsyncedDescription")}
                </span>
              )}
            </span>
            {(config.messageKey === "connection.reconnecting" ||
              config.messageKey === "connection.onlineUnsynced") && (
              <Icon aria-hidden name="Spinner" className="ml-auto mr-1.25 animate-spin" />
            )}
          </>
        )}
      </div>
      {isVisible && withBottomBorder && <div className="h-[1px] my-1.25 bg-block-border" />}
    </>
  );
});
